/* eslint-disable no-param-reassign */
import { mapGetters } from 'vuex';

/**
 * Initial state for travel restriction rules
 * @returns {Object} Default state for travel restrictions
 */
function initialState() {
  return {
    locationFrom: {
      description: '',
      detail: {
        description: '',
        coordinates: null,
      },
    },
    locationTo: {
      description: '',
      detail: {
        description: '',
        coordinates: null,
      },
    },
    location: null,
    maxAmount: {
      amount: 0,
      maxAmountRuleType: 'day',
      type: 'maxAmount',
    },
    frequency: {
      maxNumberOfBookings: 0,
      frequencyRuleType: 'day',
      type: 'frequency',
    },
    dateTime: [],
  };
}

/**
 * Mixin for managing travel restrictions
 */
export const initTravelRestrictionEntity = {
  data: () => ({
    // Pagination parameters for restriction list
    restrictionListParams: {
      page: 0,
      maxPerPage: 10,
      sort: '-createdAt',
    },
    // Track which entities have been saved
    isSavedEntities: {
      location: false,
      maxAmount: false,
      frequency: false,
    },
    // Parameters for fetching restriction rules
    restrictionRulesParams: {
      page: 0,
      maxPerPage: 10,
      sort: '-createdAt',
      type: null,
    },
    restrictionList: [],
    intersection: false,
    restrictionTotalCount: {
      totalCount: 0,
    },
    // Available rule types
    ruleTypes: ['location', 'maxAmount', 'frequency', 'dateTime'],
    // Current group rules
    groupRules: initialState(),
  }),
  
  watch: {
    restrictionListParams: {
      handler() {
        this.getTravelRestrictionList();
      },
      deep: true,
    },
    accountSelected: {
      handler() {
        this.intersection = false;
        this.restrictionListParams.page = 0;
        this.getTravelRestrictionList();
      },
    },
  },
  
  mounted() {
    this.getTravelRestrictionList();
  },
  
  created() {
    // Load the travel restriction list when the component is created
    this.getTravelRestrictionList();
  },
  
  computed: {
    ...mapGetters('accountsApiStore', ['accountSelected']),
    ...mapGetters('employee', ['getCreatedGroupId', 'getCreatedGroupName']),
    ...mapGetters('travelRestriction', ['getTravelRestrictionId', 'getTravelRestrictionRuleId']),
  },
  
  methods: {
    /**
     * Reset all travel restrictions for the current group
     * @returns {Promise<Array>} Results of deletion operations
     */
    async resetTravelRestrictions() {
      const promises = [];
      
      this.ruleTypes.forEach((type) => {
        const rule = this.getTravelRestrictionRuleId(type);

        if (rule && !Array.isArray(rule)) {
          // Single rule deletion
          const promise = this.deleteTravelRestrictionRule({
            ids: {
              ruleId: rule,
              travelRestrictionId: this.getTravelRestrictionId,
            },
            type,
          });

          promises.push(promise);
        } else if (rule && Array.isArray(rule)) {
          // Multiple rules deletion (e.g., dateTime rules)
          rule.forEach(({ id }) => {
            const promise = this.deleteTravelRestrictionRule({
              ids: {
                ruleId: id,
                travelRestrictionId: this.getTravelRestrictionId,
              },
              type,
            });

            promises.push(promise);
          });
        }
      });

      return Promise.all(promises);
    },

    /**
     * Reset group rules to initial state
     */
    resetGroupRules() {
      Object.assign(this.$data.groupRules, initialState());
    },

    /**
     * Set location rules from location data
     * @param {Object} location - Location data with origin and destination
     */
    setLocation(location) {
      this.groupRules.locationFrom = {
        description: location?.originAddress,
        detail: {
          description: location?.originAddress,
          coordinates: {
            longitude: location?.originCoordinates.coordinates[0],
            latitude: location?.originCoordinates.coordinates[0],
          },
        },
      };

      this.groupRules.locationTo = {
        description: location?.destinationAddress,
        detail: {
          description: location?.destinationAddress,
          coordinates: {
            longitude: location?.destinationCoordinates.coordinates[0],
            latitude: location?.destinationCoordinates.coordinates[0],
          },
        },
      };
    },

    /**
     * Set rules for a specific restriction ID
     * @param {string} restrictionId - ID of the restriction
     */
    async setRules(restrictionId) {
      this.$store.commit('travelRestriction/setCreatedRestrictionId', restrictionId);
      await this.storeGroupRules();
    },

    /**
     * Store group rules from the API
     */
    async storeGroupRules() {
      for (const type of this.ruleTypes) {
        this.restrictionRulesParams.type = type;
        const result = await this.getRestrictionRulesByType(this.getTravelRestrictionId);
        if (result.length === 0) {
          this.$store.commit('travelRestriction/resetRuleId', type);
          continue;
        }

        if (type === 'location') {
          this.setLocation(result[0]);
        } else {
          this.groupRules[type] = type === 'dateTime' ? result : result[0];
        }

        this.$store.commit('travelRestriction/setRuleId', { type, value: result });
      }
    },

    /**
     * Get all restriction rules for a specific ID
     * @param {string} id - Restriction ID
     * @returns {Promise<Array>} Array of restriction rules
     */
    async getRestrictionRules(id) {
      const promises = this.ruleTypes.map(type => {
        this.restrictionRulesParams.type = type;
        return this.getRestrictionRulesByType(id);
      });

      const results = await Promise.all(promises);
      return results.filter(el => el.length);
    },

    /**
     * Initialize a new restriction
     */
    async initRestriction() {
      // Check if there's already a travel restriction with the same name
      const existingRestriction = this.restrictionList.find(
        restriction => restriction.name === this.getCreatedGroupName
      );
      
      if (existingRestriction) {
        // Use the existing restriction instead of creating a new one
        this.$store.commit('travelRestriction/setCreatedRestrictionId', existingRestriction.id);
        
        // Update the employee group with the existing restriction ID
        await this.$store.dispatch('employee/updateEmployeeGroup', {
          restrictionId: existingRestriction.id,
          id: this.getCreatedGroupId,
          name: this.getCreatedGroupName,
          description: this.getCreatedGroupName,
        });
        
        return;
      }
      
      // Create a new restriction if no matching one exists
      await this.createTravelRestriction({ 
        name: this.getCreatedGroupName, 
        corporationId: this.accountSelected 
      });
    },

    /**
     * Manage rules of a specific type
     * @param {string} type - Rule type
     * @returns {Promise<boolean>} Success status
     */
    async manageRules(type) {
      // Initialize restriction if needed
      if (!this.getTravelRestrictionId && this.getCreatedGroupId) {
        // Make sure the restriction list is loaded
        if (this.restrictionList.length === 0) {
          await this.getTravelRestrictionList();
        }
        await this.initRestriction();
      }

      // Handle location rules separately
      if (type === 'location') {
        this.formatLocationRule();
        this.isSavedEntities[type] = true;
        return true;
      }

      // Prepare rule data
      const ruleData = { ...this.groupRules[type] };
      delete ruleData.id;
      delete ruleData.travelRestrictionId;

      const params = { 
        data: ruleData, 
        id: this.getTravelRestrictionId 
      };

      await this.actionRestrictionRule(params, type);
      this.isSavedEntities[type] = true;
      return true;
    },

    /**
     * Create, update, or delete a restriction rule
     * @param {Object} params - Rule parameters
     * @param {string} type - Rule type
     */
    async actionRestrictionRule(params, type) {
      const existingRule = this.getTravelRestrictionRuleId(type);

      if (existingRule || existingRule?.id) {
        // Update existing rule
        params.ruleId = existingRule?.id || existingRule;
        params.travelRestrictionId = this.getTravelRestrictionId;

        if (type === 'dateTime') {
          await this.updateTravelRestrictionRule(params);
        } else {
          // Delete and recreate for non-dateTime rules
          await this.deleteTravelRestrictionRule({
            ids: {
              ruleId: existingRule?.id || existingRule,
              travelRestrictionId: this.getTravelRestrictionId,
            },
            type,
          });
          
          delete params.ruleId;
          delete params.travelRestrictionId;

          await this.createTravelRestrictionRule(params);
        }
      } else {
        // Create new rule
        params.id = this.getTravelRestrictionId;

        if (params.data.type !== 'maxAmount' && params.data.type !== 'frequency') {
          await this.createTravelRestrictionRule(params);
        } else {
          // Only create if amount is greater than 0
          const amount = params.data.type === 'maxAmount' 
            ? this.groupRules.maxAmount.amount 
            : this.groupRules.frequency.maxNumberOfBookings;

          if (amount > 0) {
            await this.createTravelRestrictionRule(params);
          }
        }
      }
    },

    /**
     * Copy an existing restriction to a new one
     * @param {string} id - Source restriction ID
     * @param {Object} params - Parameters for the new restriction
     */
    async copyExistingRestriction(id, params) {
      if (this.getCreatedGroupId) {
        const rules = await this.getRestrictionRules(id);
        await this.setRestrictionRules(rules, params);
      }
    },

    /**
     * Set restriction rules for a new restriction
     * @param {Array} rules - Array of rules to set
     * @param {Object} params - Parameters for the new restriction
     */
    async setRestrictionRules(rules, params) {
      await this.createTravelRestriction(params);

      for (const ruleGroup of rules) {
        for (const item of ruleGroup) {
          const createParams = { 
            data: { ...item }, 
            id: this.getTravelRestrictionId 
          };
          delete createParams.data.id;

          await this.createTravelRestrictionRule(createParams);
        }
      }

      await this.storeGroupRules();
    },

    /**
     * Get restriction rules by type
     * @param {string} id - Restriction ID
     * @returns {Promise<Array>} Array of rules
     */
    async getRestrictionRulesByType(id) {
      const result = await this.$store.dispatch('travelRestriction/getTravelRestrictionRulesById', { 
        id, 
        ...this.restrictionRulesParams 
      });
      return result.data;
    },

    /**
     * Associate a group with a restriction
     * @param {string} groupId - Group ID
     * @param {string} restrictionId - Restriction ID
     * @returns {Promise<Object>} Result of the association
     */
    async associateGroupToRestriction(groupId, restrictionId) {
      const params = {
        associatedIds: [`${groupId}`],
        type: 'employeeGroups',
        restrictionId,
      };

      return this.$store.dispatch('travelRestriction/associateGroupToRestriction', params);
    },

    /**
     * Create a new travel restriction
     * @param {Object} params - Restriction parameters
     */
    async createTravelRestriction(params) {
      if (params) {
        const result = await this.$store.dispatch('travelRestriction/createTravelRestriction', params);
        
        await this.$store.dispatch('employee/updateEmployeeGroup', {
          restrictionId: result.id,
          id: this.getCreatedGroupId,
          name: params.name,
          description: params.name,
        });

        this.$store.commit('travelRestriction/setCreatedRestrictionId', result.id);
      }
    },

    /**
     * Create a new travel restriction rule
     * @param {Object} params - Rule parameters
     */
    async createTravelRestrictionRule(params) {
      const result = await this.$store.dispatch('travelRestriction/createTravelRestrictionRule', params);
      this.$store.commit('travelRestriction/setRuleId', { 
        type: params.data.type, 
        value: result 
      });
    },

    /**
     * Update an existing travel restriction rule
     * @param {Object} params - Rule parameters
     */
    async updateTravelRestrictionRule(params) {
      await this.$store.dispatch('travelRestriction/updateTravelRestrictionRule', params);
    },

    /**
     * Delete a travel restriction rule
     * @param {Object} params - Rule parameters
     * @returns {Promise<Object>} Result of the deletion
     */
    async deleteTravelRestrictionRule(params) {
      return this.$store.dispatch('travelRestriction/deleteTravelRestrictionRule', params);
    },

    /**
     * Get list of travel restrictions
     */
    async getTravelRestrictionList() {
      this.restrictionListParams.corporationId = this.accountSelected || null;
      
      // When checking for existing restrictions, load all restrictions
      if (!this.getTravelRestrictionId && this.getCreatedGroupId) {
        this.restrictionListParams.maxPerPage = 100; // Load more restrictions to ensure we find matches
      }

      const result = await this.$store.dispatch('travelRestriction/listTravelRestrictions', this.restrictionListParams);
      this.restrictionTotalCount = this.formatHeaders(result.headers);

      if (this.intersection) {
        this.restrictionList.push(...result.data);
      } else {
        this.restrictionList = result.data;
      }
      
      // Reset maxPerPage to default value
      this.restrictionListParams.maxPerPage = 10;
    },

    /**
     * Format headers from API response
     * @param {Object} headers - Response headers
     * @returns {Object} Formatted headers
     */
    formatHeaders(headers) {
      return {
        totalCount: Number(headers['x-total-count'] || 0),
      };
    },

    /**
     * Format location rule based on current group rules
     */
    formatLocationRule() {
      if (this.groupRules.locationFrom.description && this.groupRules.locationTo.description) {
        // Between locations
        const params = {
          data: {
            locationRuleType: 'between',
            type: 'location',
            originAddress: this.groupRules.locationFrom.description,
            originCoordinates: this.groupRules.locationFrom.detail.coordinates,
            destinationAddress: this.groupRules.locationTo.description,
            destinationCoordinates: this.groupRules.locationTo.detail.coordinates,
          },
          id: this.getTravelRestrictionId,
        };

        this.actionRestrictionRule(params, 'location');
      } else if (!this.groupRules.locationFrom.description) {
        // To location only
        const params = {
          data: {
            type: 'location',
            locationRuleType: 'to',
            originAddress: this.groupRules.locationTo.description,
            originCoordinates: this.groupRules.locationTo.detail.coordinates,
          },
          id: this.getTravelRestrictionId,
        };

        this.actionRestrictionRule(params, 'location');
      } else {
        // From location only
        const params = {
          data: {
            type: 'location',
            locationRuleType: 'from',
            originAddress: this.groupRules.locationFrom.description,
            originCoordinates: this.groupRules.locationFrom.detail.coordinates,
          },
          id: this.getTravelRestrictionId,
        };

        this.actionRestrictionRule(params, 'location');
      }
    },

    /**
     * Set date time rules
     * @param {Object} data - Date time data
     * @returns {Promise<string>} Action performed ('created' or 'updated')
     */
    async setDateTime(data) {
      if (!this.getTravelRestrictionId && this.getCreatedGroupId) {
        await this.initRestriction();
      }

      // Find existing dateTime rule
      const existingDateTimeRule = this.getTravelRestrictionRuleId('dateTime')
        .find((el) => el.day === data.dateTimeRuleType);

      if (existingDateTimeRule) {
        // Update existing rule
        const params = {
          data: { ...data },
          ruleId: existingDateTimeRule.id,
          travelRestrictionId: this.getTravelRestrictionId,
        };
        await this.updateTravelRestrictionRule(params);
        return 'updated';
      }

      // Create new rule
      const params = {
        data: { ...data },
        id: this.getTravelRestrictionId,
      };
      await this.createTravelRestrictionRule(params);
      return 'created';
    },
  },
};
